import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import {useMemo} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';

import {
  FetchSelectorsByIdQuery,
  FetchSelectorsByIdQueryVariables,
} from './__generated/FetchSelectorsByIdQuery';

export interface SchemaFieldSelectorsDict {
  [selectorId: string]: SchemaFieldSelector;
}

export const FETCH_SELECTORS_BY_ID_QUERY: DocumentNode = gql`
  query FetchSelectorsByIdQuery($includeArchived: StrictBoolean!) @skipAudit @engComponent(name: NOTEBOOK) {
    schemaFieldSelectors(includeArchived: $includeArchived) {
      ...SchemaFieldSelectorGraphQL
    }
  }
  ${getFragmentWrapper('SchemaFieldSelector').fragment}
`;

export function getSelectorsById(selectors: Array<SchemaFieldSelector>): SchemaFieldSelectorsDict {
  return selectors.reduce((acc, selector) => {
    acc[selector.id] = selector;
    return acc;
  }, {} as SchemaFieldSelectorsDict);
}

export function selectorsAreLoaded(
  selectorsById: SchemaFieldSelectorsDict | AsyncStatus.LOADING | AsyncStatus.ERROR
): selectorsById is SchemaFieldSelectorsDict {
  return selectorsById !== AsyncStatus.LOADING && selectorsById !== AsyncStatus.ERROR;
}

export function convertFetchSelectorsQueryResultToSchemaFieldSelectorsDict(
  data: FetchSelectorsByIdQuery
): SchemaFieldSelectorsDict {
  const schemaFieldSelectors = data.schemaFieldSelectors;

  const restSchemaFieldSelectors = graphQLArrayToREST('SchemaFieldSelector', schemaFieldSelectors);
  let selectorsById: {[id: string]: SchemaFieldSelector} = {};
  selectorsById = restSchemaFieldSelectors.reduce((acc, selector) => {
    acc[selector.id] = selector;
    return acc;
  }, selectorsById);
  return selectorsById;
}

/**
 * Query SchemaFieldSelectors and construct a selectorsById mapping for use with notebook objects.
 *
 * If 'ERROR' is returned, it will already be reported, so the caller is only responsible for appropriate
 * rendering.
 */
export default function useSelectorsById({
  includeArchivedSelectors,
}: {
  includeArchivedSelectors: boolean;
}): SchemaFieldSelectorsDict | AsyncStatus.LOADING | AsyncStatus.ERROR {
  const result = useBenchlingQuery<FetchSelectorsByIdQuery, FetchSelectorsByIdQueryVariables>(
    FETCH_SELECTORS_BY_ID_QUERY,
    {
      variables: {
        includeArchived: includeArchivedSelectors,
      },
    }
  );

  // in order to memoize the result, we need to pull the data into a separate variable for hook deps
  const data = result.type === QueryResultType.SUCCESS ? result.data : undefined;
  return useMemo(() => {
    if (result.type === QueryResultType.ERROR) {
      return AsyncStatus.ERROR;
    }
    if (result.type === QueryResultType.PENDING) {
      return AsyncStatus.LOADING;
    }
    return convertFetchSelectorsQueryResultToSchemaFieldSelectorsDict(data!);
  }, [data, result.type]);
}
